import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ConfirmModal from "../../components/ConfirmModal";
import { EmptyState } from "../../components/EmptyState";
import * as Fonts from "../../components/Fonts";
import { KeepoLoader } from "../../components/KeepoLoader";
import { useToast } from "../../components/Toast";
import ToggleSwitch from "../../components/ToggleSwitch";
import TrashIcon from "../../icons/Trash";
import { api } from "../../services/api";
import { isApiError } from "../../services/api/errors";
import {
  DOMAIN_SETTINGS_ERRORS,
  DomainOwner,
  EmailForward,
  getEmailForwarding,
  OwnedDomain,
  saveEmailForwarding,
  updateDomainOwner,
  updateDomainSecurity,
} from "../../services/domains";
import { formatPhone } from "../../utils/phone";
import { classNames } from "../../utils/styles";
import {
  BillingDetails,
  BillingDetailsValue,
  BillingErrors,
  EMPTY_BILLING,
  Field,
  validateBilling,
} from "../Subscription/pages/Checkout/BillingDetails";
import { FieldGrid } from "../Subscription/pages/Checkout/styles";
import { Panel } from "../Subscription/pages/Manage/styles";
import { Note, Records, Section, SettingGroup, SettingRow, SmallButton } from "./styles";

const prefix = "pages.domains.detail";
const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/**
 * E-mail forwarding: contato@domain lands in the person's own inbox. It only receives (sending as contato@
 * needs an e-mail service) and it uses Namecheap's e-mail, so MX records of their own turn it off.
 */
/**
 * `blockedBy`: why the forwarding can't work right now: nameservers of their own (the e-mail follows the other
 * provider's DNS) or MX records of their own (Namecheap's forwarding and own MX are one or the other).
 */
export const EmailForwarding = ({ domain, blockedBy }: { domain: string; blockedBy?: "nameservers" | "mx" }) => {
  const { t } = useTranslation();
  const [forwards, setForwards] = useState<EmailForward[]>();
  const [loadError, setLoadError] = useState(false);
  const [draft, setDraft] = useState<{ alias: string; to: string }>();
  const [error, setError] = useState<"alias" | "to" | "taken" | "rejected" | "save">();
  const [isSaving, setSaving] = useState(false);

  useEffect(() => {
    getEmailForwarding(domain)
      .then(setForwards)
      .catch(() => setLoadError(true));
  }, [domain]);

  const save = async (next: EmailForward[]) => {
    setSaving(true);
    setError(undefined);
    try {
      setForwards(await saveEmailForwarding(domain, next));
      return true;
    } catch (error) {
      // Refused (a 422) is the address or alias, not the provider being down
      setError(isApiError(error, DOMAIN_SETTINGS_ERRORS.invalid) ? "rejected" : "save");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const add = async () => {
    if (!draft || !forwards) {
      return;
    }
    const alias = draft.alias.trim().toLowerCase().replace(`@${domain}`, "");
    if (!/^[a-z0-9._-]+$/.test(alias)) {
      setError("alias");
      return;
    }
    if (!isEmail(draft.to.trim())) {
      setError("to");
      return;
    }
    if (forwards.some(item => item.alias === alias)) {
      setError("taken");
      return;
    }
    if (await save([...forwards, { alias, to: draft.to.trim() }])) {
      setDraft(undefined);
    }
  };

  return (
    <Section>
      <Fonts.Label>{t(`${prefix}.email.title`)}</Fonts.Label>
      {/* Loading or failed: inside the box, where the forwards go */}
      {loadError || !forwards ? (
        <Records aria-busy={!loadError}>
          <li className={classNames("empty", { error: loadError })}>
            {loadError ? t(`${prefix}.loadError`) : <KeepoLoader size={24} color="var(--color-neutral-white)" />}
          </li>
        </Records>
      ) : (
        <>
          <Records>
            {/* In the box itself: what's saved here won't work while this lasts */}
            {blockedBy && (
              <li className="notice" role="note">
                {t(`${prefix}.email.blocked.${blockedBy}`)}
              </li>
            )}
            {forwards.map(item => (
              <li key={item.alias}>
                <span className="record">
                  <span className="host">
                    {item.alias}@{domain}
                  </span>
                  <span className="value">→ {item.to}</span>
                </span>
                <span className="row-actions">
                  <button
                    type="button"
                    aria-label={t(`${prefix}.dns.remove`)}
                    disabled={isSaving}
                    onClick={() => save(forwards.filter(other => other.alias !== item.alias))}>
                    <TrashIcon size={16} color="currentColor" />
                  </button>
                </span>
              </li>
            ))}
            {draft && (
              <li className="editing">
                <FieldGrid>
                  <Field
                    label={t(`${prefix}.email.alias`)}
                    hint={`@${domain}`}
                    error={error === "alias" ? t(`${prefix}.email.errors.alias`) : undefined}
                    fullOnMobile>
                    <input
                      autoFocus
                      placeholder={t(`${prefix}.email.aliasPlaceholder`)}
                      value={draft.alias}
                      onChange={e => setDraft({ ...draft, alias: e.target.value })}
                    />
                  </Field>
                  <Field
                    label={t(`${prefix}.email.to`)}
                    error={error === "to" ? t(`${prefix}.email.errors.to`) : undefined}
                    fullOnMobile>
                    <input
                      type="email"
                      placeholder={t(`${prefix}.email.toPlaceholder`)}
                      value={draft.to}
                      onChange={e => setDraft({ ...draft, to: e.target.value })}
                    />
                  </Field>
                </FieldGrid>
                {(error === "taken" || error === "rejected" || error === "save") && (
                  <p className="form-error" role="alert">
                    {t(error === "save" ? `${prefix}.saveError` : `${prefix}.email.errors.${error}`)}
                  </p>
                )}
                <div className="form-actions">
                  <SmallButton type="button" disabled={isSaving} onClick={() => setDraft(undefined)}>
                    {t(`${prefix}.cancel`)}
                  </SmallButton>
                  <SmallButton type="button" $primary disabled={isSaving} $loading={isSaving} onClick={add}>
                    {t(`${prefix}.email.save`)}
                  </SmallButton>
                </div>
              </li>
            )}
            {!draft && (error === "save" || error === "rejected") && (
              <li className="notice error" role="alert">
                {t(error === "save" ? `${prefix}.saveError` : `${prefix}.email.errors.rejected`)}
              </li>
            )}
            {!forwards.length && !draft && (
              <li className="empty-state">
                <EmptyState compact inFrame emoji="✉️" title={t(`${prefix}.email.empty`)} />
              </li>
            )}
          </Records>
          {!draft && (
            <SmallButton
              type="button"
              style={{ alignSelf: "flex-start" }}
              onClick={async () => {
                setError(undefined);
                const user = await api.getUser().catch(() => undefined);
                setDraft({ alias: "", to: user?.email || "" });
              }}>
              + {t(`${prefix}.email.add`)}
            </SmallButton>
          )}
        </>
      )}
      <Note>{t(`${prefix}.email.note`)}</Note>
    </Section>
  );
};

/**
 * Security: the transfer lock and WHOIS privacy (both on by default), and how to move the domain to another
 * registrar. Namecheap's API doesn't hand out the transfer (EPP) code: it goes through support.
 */
export const Security = ({ owned, onChange }: { owned: OwnedDomain; onChange: (next: OwnedDomain) => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPrivacyOffOpen, setPrivacyOffOpen] = useState(false);
  const [isTransferOpen, setTransferOpen] = useState(false);
  // The toggle being saved and its new position: it moves at once, with a loader on its left until the API
  // answers (back where it was if it fails)
  const [pending, setPending] = useState<{ field: "locked" | "privacy"; value: boolean }>();
  const isSaving = !!pending;
  const toast = useToast();

  // Saved right away: a toast says it's on or off, or that it couldn't be changed
  const update = async (field: "locked" | "privacy", value: boolean) => {
    setPending({ field, value });
    try {
      const next = await updateDomainSecurity(owned.domain, { [field]: value });
      onChange(next);
      const name = field === "locked" ? "lock" : "privacy";
      // what the API kept (the registrar may not have done what was asked), not what was asked
      toast.success(t(`${prefix}.security.${name}.${next[field] ? "turnedOn" : "turnedOff"}`));
    } catch {
      toast.error(t(`${prefix}.saveError`));
    } finally {
      setPending(undefined);
    }
  };

  const toggle = (field: "locked" | "privacy", onToggle: (value: boolean) => void) => (
    <span className="control">
      {pending?.field === field && <KeepoLoader size={12} color="var(--color-neutral-white)" />}
      <ToggleSwitch
        label={t(`${prefix}.security.${field === "locked" ? "lock" : "privacy"}.title`)}
        value={pending?.field === field ? pending.value : owned[field]}
        disabled={isSaving}
        setValue={onToggle}
      />
    </span>
  );

  return (
    <Section>
      <Fonts.Label>{t(`${prefix}.security.title`)}</Fonts.Label>
      <SettingGroup>
        <SettingRow>
          <span className="text">
            <b>{t(`${prefix}.security.lock.title`)}</b>
            <span>{t(`${prefix}.security.lock.${owned.locked ? "on" : "off"}`)}</span>
          </span>
          {toggle("locked", locked => update("locked", locked))}
        </SettingRow>
        <SettingRow>
          <span className="text">
            <b>{t(`${prefix}.security.privacy.title`)}</b>
            <span>{t(`${prefix}.security.privacy.${owned.privacy ? "on" : "off"}`)}</span>
          </span>
          {/* Turning it off shows the owner's details to anyone: asked first */}
          {toggle("privacy", value => (value ? update("privacy", true) : setPrivacyOffOpen(true)))}
        </SettingRow>
        <SettingRow>
          <span className="text">
            <b>{t(`${prefix}.security.transfer.title`)}</b>
            <span>{t(`${prefix}.security.transfer.text`)}</span>
          </span>
          <SmallButton type="button" onClick={() => setTransferOpen(true)}>
            {t(`${prefix}.security.transfer.button`)}
          </SmallButton>
        </SettingRow>
      </SettingGroup>

      {isPrivacyOffOpen && (
        <ConfirmModal
          type="DANGER"
          title={t(`${prefix}.security.privacy.modal.title`)}
          descriptions={[t(`${prefix}.security.privacy.modal.text`)]}
          confirmButtonTitle={t(`${prefix}.security.privacy.modal.confirm`)}
          cancelTitle={t(`${prefix}.cancel`)}
          onConfirm={() => {
            setPrivacyOffOpen(false);
            update("privacy", false);
          }}
          onCancel={() => setPrivacyOffOpen(false)}
        />
      )}
      {isTransferOpen && (
        <ConfirmModal
          type="INFO"
          title={t(`${prefix}.security.transfer.modal.title`)}
          descriptions={[
            t(`${prefix}.security.transfer.modal.unlock`),
            t(`${prefix}.security.transfer.modal.code`),
            t(`${prefix}.security.transfer.modal.rule`),
          ]}
          confirmButtonTitle={t(`${prefix}.security.transfer.modal.confirm`)}
          cancelTitle={t(`${prefix}.cancel`)}
          onConfirm={() => navigate("/help")}
          onCancel={() => setTransferOpen(false)}
        />
      )}
    </Section>
  );
};

type OwnerProps = {
  domain: string;
  owner?: Partial<DomainOwner>;
  onChange: (next: OwnedDomain & { owner?: Partial<DomainOwner> }) => void;
};

/**
 * The domain's owner (registrant): the details given when buying it. Kept private by WHOIS privacy; changing the
 * name or e-mail may lock transfers for 60 days (ICANN's change of registrant rule).
 */
export const Owner = ({ domain, owner, onChange }: OwnerProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<{ billing: BillingDetailsValue; email: string; phone: string }>();
  const [isSaving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  // What's missing or wrong, as at checkout (the API checks again)
  const [billingErrors, setBillingErrors] = useState<BillingErrors>({});
  const [contactErrors, setContactErrors] = useState<{ email?: boolean; phone?: boolean }>({});

  const edit = () => {
    setError(false);
    setBillingErrors({});
    setContactErrors({});
    setDraft({
      billing: { ...EMPTY_BILLING, ...(owner as Partial<BillingDetailsValue>) },
      email: owner?.email || "",
      phone: owner?.phone || "",
    });
  };

  const save = async () => {
    if (!draft) {
      return;
    }
    const nextBillingErrors = validateBilling(draft.billing, draft.billing.country === "BR" ? "address" : "none");
    // The CPF/CNPJ isn't kept after buying (the API doesn't hand it back): empty is fine, wrong isn't
    if (!draft.billing.document.replace(/\D/g, "")) {
      delete nextBillingErrors.document;
    }
    const nextContactErrors = {
      email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim()),
      phone: draft.phone.replace(/\D/g, "").length < 8,
    };
    setBillingErrors(nextBillingErrors);
    setContactErrors(nextContactErrors);
    if (Object.keys(nextBillingErrors).length || nextContactErrors.email || nextContactErrors.phone) {
      return;
    }
    setSaving(true);
    setError(false);
    try {
      onChange(
        await updateDomainOwner(domain, { ...draft.billing, email: draft.email.trim(), phone: draft.phone.trim() })
      );
      setDraft(undefined);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section>
      <Fonts.Label>{t(`${prefix}.owner.title`)}</Fonts.Label>
      {draft ? (
        // The form groups' card (as the billing details'); e-mail and phone right under the name and CPF, as
        // at checkout
        <Panel>
          <BillingDetails
            value={draft.billing}
            onChange={billing => setDraft({ ...draft, billing })}
            errors={billingErrors}
            withBrazilFields
            afterDocument={
              <>
                <Field
                  label={t("pages.domains.checkout.owner.email")}
                  error={contactErrors.email ? t("pages.domains.checkout.errors.email") : undefined}
                  fullOnMobile>
                  <input
                    type="email"
                    value={draft.email}
                    onChange={e => setDraft({ ...draft, email: e.target.value })}
                  />
                </Field>
                <Field
                  label={t("pages.domains.checkout.owner.phone")}
                  error={contactErrors.phone ? t("pages.domains.checkout.errors.phone") : undefined}
                  fullOnMobile>
                  <input
                    type="tel"
                    placeholder={draft.billing.country === "BR" ? "(48) 99999-0000" : "+1 555 123 4567"}
                    value={draft.phone}
                    onChange={e => setDraft({ ...draft, phone: formatPhone(e.target.value, draft.billing.country) })}
                  />
                </Field>
              </>
            }
          />
          <Note className="warning">{t(`${prefix}.owner.warning`)}</Note>
          {error && <Note className="warning">{t(`${prefix}.saveError`)}</Note>}
          <span style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <SmallButton type="button" disabled={isSaving} onClick={() => setDraft(undefined)}>
              {t(`${prefix}.cancel`)}
            </SmallButton>
            <SmallButton type="button" $primary disabled={isSaving} $loading={isSaving} onClick={save}>
              {t(`${prefix}.owner.save`)}
            </SmallButton>
          </span>
        </Panel>
      ) : (
        <SettingRow>
          <span className="text">
            <b>{owner?.name || "—"}</b>
            <span>
              {owner?.email}
              {owner?.city ? ` · ${owner.city}/${owner.state}` : ""}
            </span>
          </span>
          <SmallButton type="button" onClick={edit}>
            {t(`${prefix}.owner.edit`)}
          </SmallButton>
        </SettingRow>
      )}
      <Note>{t(`${prefix}.owner.note`)}</Note>
    </Section>
  );
};
